import { useCallback, useEffect, useState } from "react";
import { fetchFoodBasketStapleTargetPlan } from "../../../../api/foodApi";
import {
  foodRetailSourceLabel,
  isFoodRetailPlaybookSource,
} from "../../../../constants/foodSourceConstants";
import AdminTableContainer from "../layout/AdminTableContainer";

const FoodAraStapleTargetPlanSection = ({ sourceKey }) => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const sourceLabel = foodRetailSourceLabel(sourceKey);

  const loadPlan = useCallback(async () => {
    if (!sourceKey || !isFoodRetailPlaybookSource(sourceKey)) {
      setPlan(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");
      const payload = await fetchFoodBasketStapleTargetPlan({ sourceKey });
      setPlan(payload);
    } catch (error) {
      setErrorMessage(error.message || "Failed to load staple target plan.");
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [sourceKey]);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  const summary = plan?.summary;

  if (!sourceKey || !isFoodRetailPlaybookSource(sourceKey)) {
    return null;
  }

  return (
    <section className="panel-card food-retail-ingestion-page__normalization">
      <div className="food-retail-ingestion-page__section-header">
        <div>
          <h2>{sourceLabel} Staple Target Plan</h2>
          <p className="muted">
            Read-only collection plan from the coverage baseline: priority tiers, search terms,
            fixture suggestions, and strategies. Does not run ingestion.
          </p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={loadPlan} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh plan"}
        </button>
      </div>

      {errorMessage ? <p className="form-error">{errorMessage}</p> : null}
      {loading && !plan ? <p className="muted">Loading target plan…</p> : null}

      {plan ? (
        <>
          <div className="food-retail-ingestion-page__kpi-row">
            <div>
              <span className="muted">Coverage</span>
              <strong>
                {summary.pricedItemCount} / {summary.basketItemCount} ({summary.coveragePercent}%)
              </strong>
            </div>
            <div>
              <span className="muted">Status</span>
              <strong>{summary.latestBasketStatus || "—"}</strong>
            </div>
            <div>
              <span className="muted">P0 missing</span>
              <strong>{summary.p0MissingCount}</strong>
            </div>
            <div>
              <span className="muted">P1 missing</span>
              <strong>{summary.p1MissingCount}</strong>
            </div>
            <div>
              <span className="muted">P2 missing</span>
              <strong>{summary.p2MissingCount}</strong>
            </div>
          </div>

          {plan.cityGateNote ? (
            <p className="muted food-retail-ingestion-page__hint">{plan.cityGateNote}</p>
          ) : null}

          {summary.recommendedImmediateTargets?.length ? (
            <div className="food-retail-ingestion-page__hint">
              <strong>Immediate targets</strong>
              <ol>
                {summary.recommendedImmediateTargets.map((name) => {
                  const target = plan.targets.find((t) => t.canonicalProductName === name);
                  if (!target) return null;
                  return (
                    <li key={name}>
                      {name} — {target.currentGapReason} — {target.collectionStrategy} — search:{" "}
                      {(target.spanishSearchTerms || []).slice(0, 4).join(", ")}
                      {target.fixtureNameSuggestion
                        ? ` — fixture: ${target.fixtureNameSuggestion}`
                        : ""}
                    </li>
                  );
                })}
              </ol>
            </div>
          ) : null}

          {["P0", "P1", "P2"].map((tier) => (
            <div key={tier}>
              <h3>{tier} staples</h3>
              <AdminTableContainer>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Staple</th>
                      <th>Gap</th>
                      <th>Strategy</th>
                      <th>Action</th>
                      <th>Search terms</th>
                      <th>Fixture</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(plan.priorityGroups[tier] || []).map((target) => (
                      <tr key={target.canonicalProductName}>
                        <td>{target.canonicalProductName}</td>
                        <td>{target.currentGapReason}</td>
                        <td>{target.collectionStrategy}</td>
                        <td>{target.recommendedAction}</td>
                        <td>{(target.spanishSearchTerms || []).join(", ")}</td>
                        <td>{target.fixtureNameSuggestion || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </AdminTableContainer>
            </div>
          ))}
        </>
      ) : null}
    </section>
  );
};

export default FoodAraStapleTargetPlanSection;
